import {
  Block as SkipIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useCompleteNextActionMutation } from "../../store/services/nextActionService";
import {
  useGetWorkItemQuery,
  usePatchWorkItemMutation,
} from "../../store/services/workItemService";
import { WorkItemTypeEnum } from "../../store/services/timelineApiRTK";
import {
  ACTION_RESULTS,
  WORK_ITEM_CATEGORIES,
  WorkItemCategory,
  getWorkItemColor,
  getWorkItemIcon,
  getWorkItemTypeName,
} from "../../utils/workItemUtils";

interface CompleteActionDialogProps {
  open: boolean;
  customerNo: number;
  workItemNo: number;
  onClose: () => void;
  onCancel?: () => void;
  onComplete: () => void;
  onCompleteWithNextAction?: () => void;
}

type Outcome = "completed" | "skipped";

const OUTCOMES: Array<{
  value: Outcome;
  label: string;
  icon: React.ReactNode;
  fg: string;
  bg: string;
  result: string;
}> = [
  {
    value: "completed",
    label: "Genomförd",
    icon: <CheckIcon />,
    fg: "#2F7D54",
    bg: "#E6F2EC",
    result: ACTION_RESULTS.COMPLETED,
  },
  {
    value: "skipped",
    label: "Skippa",
    icon: <SkipIcon />,
    fg: "#2F7D54",
    bg: "#E6F2EC",
    result: ACTION_RESULTS.SKIPPED,
  },
];

const getCategoryForType = (type: WorkItemTypeEnum): WorkItemCategory | null => {
  if (WORK_ITEM_CATEGORIES[WorkItemCategory.COMMUNICATION].includes(type))
    return WorkItemCategory.COMMUNICATION;
  if (WORK_ITEM_CATEGORIES[WorkItemCategory.FOLLOWUP].includes(type))
    return WorkItemCategory.FOLLOWUP;
  return null;
};

const CompleteActionDialog: React.FC<CompleteActionDialogProps> = ({
  open,
  onClose,
  customerNo,
  onComplete,
  workItemNo,
  onCancel,
  onCompleteWithNextAction,
}) => {
  const theme = useTheme();
  const [outcome, setOutcome] = useState<Outcome>("completed");
  const [comment, setComment] = useState<string>("");
  const [selectedType, setSelectedType] = useState<WorkItemTypeEnum | null>(
    null
  );

  // Fetch the work item — uses RTK Query cache if the parent already loaded it.
  const { data: workItem } = useGetWorkItemQuery(
    { workItemId: workItemNo },
    { skip: !open || !workItemNo }
  );

  // Initialise selectedType from the loaded work item on first data arrival.
  React.useEffect(() => {
    if (workItem?.Type != null && selectedType == null) {
      setSelectedType(workItem.Type);
    }
  }, [workItem?.Type, selectedType]);

  const [completeNextAction, { isLoading: isCompleting }] =
    useCompleteNextActionMutation();
  const [patchWorkItem, { isLoading: isPatching }] =
    usePatchWorkItemMutation();
  const isLoading = isCompleting || isPatching;

  const category = workItem ? getCategoryForType(workItem.Type) : null;
  // Endast Planering (Communication) får byta typ vid slutförande.
  // Mötesbokning (Followup) är ett kundåtagande och låser typen.
  // Kontaktförsök läggs till som extra val: när man försökt nå kunden men
  // inte lyckats, stäng aktiviteten som ett kontaktförsök (effort-metrik).
  const availableTypes: WorkItemTypeEnum[] =
    category === WorkItemCategory.COMMUNICATION
      ? [
          ...WORK_ITEM_CATEGORIES[WorkItemCategory.COMMUNICATION],
          WorkItemTypeEnum.ContactAttempt,
        ]
      : [];
  const typeChanged =
    !!workItem && selectedType != null && selectedType !== workItem.Type;

  const isSkipped = outcome === "skipped";
  const selectedOutcome = OUTCOMES.find((o) => o.value === outcome)!;

  const handleSubmit = async (withNextAction: boolean) => {
    try {
      if (typeChanged && workItem && selectedType != null) {
        // If the title was auto-generated from the original type's name,
        // swap it for the new type's name. Otherwise keep the user's custom title.
        const titleIsAutoGenerated =
          workItem.Name === getWorkItemTypeName(workItem.Type);

        await patchWorkItem({
          workItemId: workItem.Id,
          patch: {
            Type: selectedType,
            ...(titleIsAutoGenerated
              ? { Name: getWorkItemTypeName(selectedType) }
              : {}),
          },
        }).unwrap();
      }

      await completeNextAction({
        customerNo,
        res: {
          WorkItemNo: workItemNo,
          WorkItemResult: { Result: selectedOutcome.result, Comment: comment },
        },
      }).unwrap();

      if (withNextAction && onCompleteWithNextAction) {
        onCompleteWithNextAction();
      } else {
        onComplete();
      }
    } catch (err) {
      console.error("Error completing action:", err);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isSkipped ? "Skippa aktivitet" : "Slutför aktivitet"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isSkipped
            ? "Aktiviteten arkiveras utan att räknas som genomförd."
            : "Markera aktiviteten som utförd."}
        </Typography>

        {/* Outcome buttons */}
        <Stack direction="row" gap={1} sx={{ mb: 2.5 }}>
          {OUTCOMES.map((opt) => {
            const active = outcome === opt.value;
            return (
              <ButtonBase
                key={opt.value}
                onClick={() => setOutcome(opt.value)}
                sx={{
                  flex: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: "10px",
                  border: `1.5px solid ${active ? opt.fg : theme.palette.divider}`,
                  bgcolor: active ? opt.bg : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.85,
                  transition: "all .15s ease",
                  "&:hover": {
                    borderColor: opt.fg,
                    bgcolor: alpha(opt.bg, 0.6),
                  },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    placeItems: "center",
                    color: active ? opt.fg : theme.palette.text.secondary,
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                  }}
                >
                  {opt.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: active ? opt.fg : "text.primary",
                  }}
                >
                  {opt.label}
                </Typography>
              </ButtonBase>
            );
          })}
        </Stack>

        {/* Type change row */}
        {workItem && availableTypes.length > 0 && !isSkipped && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: ".06em",
                display: "block",
                mb: 0.75,
              }}
            >
              Typ av aktivitet
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {availableTypes.map((t) => {
                const active = selectedType === t;
                const color = getWorkItemColor(t);
                return (
                  <ButtonBase
                    key={t}
                    onClick={() => setSelectedType(t)}
                    sx={{
                      px: 1.25,
                      py: 0.85,
                      gap: 0.65,
                      borderRadius: "10px",
                      border: `1px solid ${active ? color : theme.palette.divider}`,
                      bgcolor: active ? alpha(color, 0.1) : "transparent",
                      color: active ? color : "text.secondary",
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      "& .MuiSvgIcon-root": {
                        fontSize: 16,
                        color: active ? color : "text.disabled",
                      },
                      "&:hover": {
                        borderColor: color,
                        color: color,
                        "& .MuiSvgIcon-root": { color },
                      },
                    }}
                  >
                    {getWorkItemIcon(t)}
                    {getWorkItemTypeName(t)}
                  </ButtonBase>
                );
              })}
            </Stack>
            {typeChanged && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: theme.palette.warning.dark,
                  mt: 0.75,
                }}
              >
                Aktivitetens typ ändras från{" "}
                <strong>{getWorkItemTypeName(workItem.Type)}</strong> till{" "}
                <strong>
                  {selectedType ? getWorkItemTypeName(selectedType) : ""}
                </strong>
                .
              </Typography>
            )}
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          label="Kommentar"
          variant="outlined"
          multiline
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Avbryt</Button>
        {onCompleteWithNextAction && (
          <Button
            onClick={() => handleSubmit(true)}
            variant="outlined"
            color="primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : isSkipped ? (
              "Skippa & planera nästa"
            ) : (
              "Slutför & planera nästa"
            )}
          </Button>
        )}
        <Button
          onClick={() => handleSubmit(false)}
          variant="contained"
          color="success"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : isSkipped ? (
            "Skippa"
          ) : (
            "Slutför"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteActionDialog;
